import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { Fragment, type ReactNode } from 'react'

export interface SelectOption<T extends string = string> {
  id: T
  label: string
}

interface BaseSelectProps<T extends string> {
  options: readonly SelectOption<T>[]
  triggerLabel: string
  ariaLabel: string
  className?: string
  disabled?: boolean
}

export interface SelectProps<T extends string> extends BaseSelectProps<T> {
  value: T | null
  onChange: (value: T) => void
}

export interface MultiSelectProps<T extends string> extends BaseSelectProps<T> {
  value: readonly T[]
  onChange: (value: T[]) => void
}

const TRIGGER_CLASSES =
  'relative w-full cursor-pointer rounded-xl bg-navy-light py-4 pl-4 pr-12 text-left text-white shadow-sm transition-all hover:bg-navy-light/80 focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-navy disabled:cursor-not-allowed disabled:opacity-60'

const PANEL_CLASSES =
  'absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-xl bg-navy-dark py-2 text-base shadow-xl ring-1 ring-neon-cyan/40 focus:outline-none'

function TriggerContents({ label }: { label: string }) {
  return (
    <>
      <span className="block truncate text-base font-medium">{label}</span>
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
        <ChevronDownIcon className="h-5 w-5 text-white/60" aria-hidden="true" />
      </span>
    </>
  )
}

function OptionsTransition({ children }: { children: ReactNode }) {
  return (
    <Transition
      as={Fragment}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      {children}
    </Transition>
  )
}

function singleOptionClass({ active, selected }: { active: boolean; selected: boolean }): string {
  const base = 'relative cursor-pointer select-none py-3 pr-4 focus:outline-none'
  const selection = selected
    ? 'border-l-2 border-neon-cyan bg-neon-cyan/10 pl-[calc(1rem-2px)] font-semibold'
    : 'pl-4 font-normal'
  const activeState = active ? 'bg-neon-cyan/20 text-neon-cyan' : 'text-white'
  return `${base} ${selection} ${activeState}`
}

function multiOptionClass({ active }: { active: boolean }): string {
  const base = 'relative cursor-pointer select-none py-3 px-4 focus:outline-none'
  const activeState = active ? 'bg-neon-cyan/20 text-neon-cyan' : 'text-white'
  return `${base} ${activeState}`
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  triggerLabel,
  ariaLabel,
  className,
  disabled,
}: SelectProps<T>) {
  const triggerClassName = className ? `${TRIGGER_CLASSES} ${className}` : TRIGGER_CLASSES
  return (
    <Listbox value={value ?? undefined} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            className={triggerClassName}
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-haspopup="listbox"
          >
            <TriggerContents label={triggerLabel} />
          </Listbox.Button>
          <OptionsTransition>
            <Listbox.Options className={PANEL_CLASSES} aria-label={`${ariaLabel} options`}>
              {options.map((option) => (
                <Listbox.Option
                  key={option.id}
                  value={option.id}
                  className={({ active, selected }) => singleOptionClass({ active, selected })}
                >
                  {({ selected }) => (
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                      {option.label}
                    </span>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </OptionsTransition>
        </div>
      )}
    </Listbox>
  )
}

export function MultiSelect<T extends string>({
  value,
  onChange,
  options,
  triggerLabel,
  ariaLabel,
  className,
  disabled,
}: MultiSelectProps<T>) {
  const triggerClassName = className ? `${TRIGGER_CLASSES} ${className}` : TRIGGER_CLASSES
  const selectedSet = new Set(value)
  const handleChange = (next: T[]) => onChange(next)
  return (
    <Listbox value={value as T[]} onChange={handleChange} disabled={disabled} multiple>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            className={triggerClassName}
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-haspopup="listbox"
          >
            <TriggerContents label={triggerLabel} />
          </Listbox.Button>
          <OptionsTransition>
            <Listbox.Options
              className={PANEL_CLASSES}
              aria-label={`${ariaLabel} options`}
              aria-multiselectable="true"
            >
              {options.map((option) => {
                const isSelected = selectedSet.has(option.id)
                return (
                  <Listbox.Option
                    key={option.id}
                    value={option.id}
                    className={({ active }) => multiOptionClass({ active })}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                          isSelected
                            ? 'border-neon-cyan bg-neon-cyan'
                            : 'border-white/30 bg-transparent'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && <CheckIcon className="h-3 w-3 text-navy" />}
                      </span>
                      <span className={`block flex-1 ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                        {option.label}
                      </span>
                    </div>
                  </Listbox.Option>
                )
              })}
            </Listbox.Options>
          </OptionsTransition>
        </div>
      )}
    </Listbox>
  )
}
